import { authClient } from './auth.ts';
import { store } from './store/index.ts';

/**
 * The parent's sign-in state, independent of which backend the build uses, so screens never
 * import an auth SDK. Local (prototype) mode has no accounts and is always "signed in".
 */
export type AuthStatus = 'loading' | 'signed_in' | 'signed_out';
export interface AuthSession {
  status: AuthStatus;
  email: string | null;
}

const LOCAL: AuthSession = { status: 'signed_in', email: null };
const LOADING: AuthSession = { status: 'loading', email: null };
const SIGNED_OUT: AuthSession = { status: 'signed_out', email: null };

function useLocalSession(): AuthSession {
  return LOCAL;
}

function useApiSession(): AuthSession {
  const { data, isPending } = authClient.useSession();
  if (isPending) return LOADING;
  return data ? { status: 'signed_in', email: data.user.email } : SIGNED_OUT;
}

export const useAuthSession: () => AuthSession =
  store.kind === 'api' ? useApiSession : useLocalSession;

/** Email a sign-in link. Resolves with an error message, or null once the email is on its way. */
export async function signInWithMagicLink(email: string): Promise<string | null> {
  if (store.kind === 'api') {
    const { error } = await authClient.signIn.magicLink({ email, callbackURL: '/' });
    return error?.message ?? null;
  }
  return null;
}

/** Start the Google OAuth redirect. Resolves with an error message if it could not begin. */
export async function signInWithGoogle(): Promise<string | null> {
  if (store.kind === 'api') {
    const { error } = await authClient.signIn.social({ provider: 'google', callbackURL: '/' });
    return error?.message ?? null;
  }
  return null;
}
